/**
 * Sign-offs (services/signOff, api/signOff on the API): one request for one
 * person to sign off on one thing. Shared words and rules of the screens.
 */

export const SIGN_OFF_TYPE = Object.freeze({
    ACTION_ITEM: "actionItem",
    MISSION: "mission",
    PLAN_ITEM: "planItem",
    BUDGET: "budget",
    BASELINE: "baseline",
    VENDOR_INVOICE: "vendorInvoice",
    CLIENT_INVOICE: "clientInvoice",
})

export const SIGN_OFF_STATUS = Object.freeze({
    REQUESTED: "requested",
    READY: "ready",
    APPROVED: "approved",
    REJECTED: "rejected",
    CANCELLED: "cancelled",
})

/** Still waiting on somebody. */
export const SIGN_OFF_OPEN_STATUSES = Object.freeze([
    SIGN_OFF_STATUS.REQUESTED,
    SIGN_OFF_STATUS.READY,
    SIGN_OFF_STATUS.REJECTED,
])

export const SIGN_OFF_STATUS_LABEL = Object.freeze({
    [SIGN_OFF_STATUS.REQUESTED]: "Requested",
    // Not shown as a pill (SignOffStatus): the status filters still use it
    [SIGN_OFF_STATUS.READY]: "Ready for approval",
    [SIGN_OFF_STATUS.APPROVED]: "Approved",
    [SIGN_OFF_STATUS.REJECTED]: "Rejected",
    [SIGN_OFF_STATUS.CANCELLED]: "Cancelled",
})

export const SIGN_OFF_TYPE_LABEL = Object.freeze({
    [SIGN_OFF_TYPE.ACTION_ITEM]: "Task",
    [SIGN_OFF_TYPE.MISSION]: "Project",
    [SIGN_OFF_TYPE.PLAN_ITEM]: "Plan item",
    [SIGN_OFF_TYPE.BUDGET]: "Budget",
    [SIGN_OFF_TYPE.BASELINE]: "Baseline",
    [SIGN_OFF_TYPE.VENDOR_INVOICE]: "Vendor invoice",
    [SIGN_OFF_TYPE.CLIENT_INVOICE]: "Client invoice",
})

/** The notification types of sign-offs, with the words of the settings screen. */
export const SIGN_OFF_NOTIFICATION_TYPES = Object.freeze({
    signoff_ready: "Ready for your approval",
    signoff_approved: "Sign-off approved",
    signoff_rejected: "Sign-off rejected / approval withdrawn",
    signoff_cancelled: "Sign-off cancelled",
})

export const isSignOffNotification = (type) => typeof type === "string" && type.startsWith("signoff_")

export const isSignOffOpen = (signOff) => SIGN_OFF_OPEN_STATUSES.includes(signOff?.status)

/**
 * @param signOff {Object}
 * @param personId {String}
 * @return {boolean} Whether this person is asked to sign it
 */
export const isSignOffApprover = (signOff, personId) =>
    !!personId && String(signOff?.approver?.ref || "") === String(personId)

export const isSignOffRequester = (signOff, personId) =>
    !!personId && String(signOff?.requestedBy?.ref || "") === String(personId)

/** The one line the inbox shows for a sign-off. */
export const signOffLine = (signOff) => [signOff?.title, signOff?.description].filter(Boolean).join(": ") || "Sign-off"

/**
 * What a person may do with a sign-off, the way the API decides it
 * (api/signOff/signOff.rules.js), for screens that list many at once.
 *
 * @param signOff {Object}
 * @param ctx {{ personId: String, orgUser?: Object, missionUser?: Object }}
 * @return {{ edit: boolean, decide: boolean, withdraw: boolean, cancel: boolean }}
 */
export function signOffAbilities(signOff, { personId, orgUser, missionUser } = {}) {
    const isAdmin = orgUser?.permission === 2
    const isManager = !!missionUser && (missionUser.permission === 2 || !!missionUser.isProjectManager)
    const requester = isSignOffRequester(signOff, personId)
    const approver = isSignOffApprover(signOff, personId)
    const open = isSignOffOpen(signOff)

    return {
        edit: open && (requester || isAdmin || isManager),
        decide: open && approver,
        withdraw: signOff?.status === SIGN_OFF_STATUS.APPROVED && approver,
        cancel: open && (requester || isAdmin || isManager),
    }
}
